import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import httpRequest from "../../../ajax";
import Layout from "../../../layout";
import { postExternalMatchScore } from "../../../services/matchScoreService";
import { useHistory } from "react-router-dom";
import ProHunterUpsellBanner from "../../../components/ProHunterUpsellBanner";
import ProHunterTrialBanner from "../../../components/ProHunterTrialBanner";
import CustomModal from "../../../components/CustomModal";
import ProHunterTrialEndModal from "../../../components/ProHunterTrialEndModal";
import { numberWithCommas, uploadFile } from "../../../utils/helpers";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import HistoryIcon from "@material-ui/icons/History";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import CloseIcon from "@material-ui/icons/Close";
import { CircularProgress } from "@material-ui/core";
import {
  buyScanCredits as buyScanCreditsAction,
  claimCampaignCredits as claimCampaignCreditsAction,
  saveMatchScore as saveMatchScoreAction,
  logMatchScoreRun as logMatchScoreRunAction,
  getMatchScoreHistory as getMatchScoreHistoryAction,
  subscribeToJobSeekerPlan,
  convertProHunterTrialJarapay,
  submitProHunterTrialFeedback,
} from "../../../redux/actions/jobSeekerSubscriptionActions";
import { trackEvent, trackMatchScore, trackFundWalletClick } from "../../../utils/analytics";

import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../api/queryKeys";
import {
  useProfile,
  useJobSeekerSubscription,
  useActiveCampaign,
  useJobSeekerPlans,
  useProHunterTrial,
} from "../../../hooks/queries";
import { getProfileAction } from "../../../redux/actions/profileActions";
import { GET_PROFILE, ERROR } from "../../../redux/actionTypes";
import { MATCH_PREVIEW_CONTINUE_STORAGE_KEY } from "../../../constants/matchPreviewHandoff";
import "./index.scss";
import { useTourGuide } from "../../../hooks/useTourGuide";

const JobMatchScore = () => {
  useTourGuide();
  const [activeTab, setActiveTab] = useState("description");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [atsOpen, setAtsOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  const [buyCreditMethod, setBuyCreditMethod] = useState("wallet");
  const [buyingCredit, setBuyingCredit] = useState(false);
  const [pendingAnalyzeAfterBuy, setPendingAnalyzeAfterBuy] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [claimingCampaign, setClaimingCampaign] = useState(false);
  const [campaignMediumReasoningCredits, setCampaignMediumReasoningCredits] =
    useState(0);
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [uploadingResume, setUploadingResume] = useState(false);
  const [previewHandoffNotice, setPreviewHandoffNotice] = useState(false);
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  const [showTrialEndModal, setShowTrialEndModal] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const { userData } = useSelector((store) => store.auth);
  const { profileData } = useSelector((store) => store.profile || {});
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const resumeInputRef = useRef(null);
  const {
    isLoading: profileQueryLoading,
    refetch: refetchProfile,
  } = useProfile(Boolean(userData?.id));
  const {
    data: subscriptionFromQuery,
    isLoading: subscriptionQueryLoading,
  } = useJobSeekerSubscription(Boolean(userData?.id));
  const { data: campaignFromQuery } = useActiveCampaign(Boolean(userData?.id));
  const { data: plansFromQuery, isLoading: plansQueryLoading } = useJobSeekerPlans(Boolean(userData?.id));
  const { data: trialData } = useProHunterTrial(Boolean(userData?.id));

  const refreshSubscription = async () => {
    await queryClient.invalidateQueries(queryKeys.jobSeekerSubscription);
    return subscriptionFromQuery;
  };

  const invalidateProfileQuery = async () => {
    await queryClient.invalidateQueries(queryKeys.profile);
  };

  const invalidateCampaignQuery = async () => {
    await queryClient.invalidateQueries(queryKeys.activeCampaign);
  };

  useEffect(() => {
    if (subscriptionFromQuery !== undefined) {
      setSubscriptionData(subscriptionFromQuery);
    }
  }, [subscriptionFromQuery]);

  useEffect(() => {
    setCampaign(campaignFromQuery || null);
  }, [campaignFromQuery]);

  useEffect(() => {
    if (plansFromQuery) {
      setPlans(plansFromQuery);
    }
  }, [plansFromQuery]);

  useEffect(() => {
    if (trialData !== undefined) {
      setTrialEligible(Boolean(trialData?.eligible && trialData?.payment_rail === "stripe"));
    }
  }, [trialData]);

  useEffect(() => {
    setLoading(profileQueryLoading || subscriptionQueryLoading || plansQueryLoading);
  }, [profileQueryLoading, subscriptionQueryLoading, plansQueryLoading]);
  // Temporary local toggles for UI/API testing.
  // Keep `enabled: false` in normal usage.
  const TEST_FLAGS = {
    enabled: false,
    forceIsFreeForApi: true, // true | false | null
    forceShowCampaignBanner: false, // true | false | null
    forceIsProHunter: false, // true | false | null
  };

  useEffect(() => {
    const init = async () => {
      try {
        const params = new URLSearchParams(history.location.search);
        if (params.get("activate_trial") === "1") {
          history.replace("/pro-hunter-trial");
          return;
        }
        if (params.get("trial") === "activated") {
          setShowTrialWelcome(true);
          history.replace("/job-match-score");
        }
        const profileAction = profileData
          ? { type: GET_PROFILE, payload: profileData }
          : await getProfileAction(dispatch);

        try {
          const handoffRaw = sessionStorage.getItem(
            MATCH_PREVIEW_CONTINUE_STORAGE_KEY,
          );
          if (
            handoffRaw &&
            profileAction?.type === GET_PROFILE &&
            profileAction.payload
          ) {
            let h;
            try {
              h = JSON.parse(handoffRaw);
            } catch (parseErr) {
              sessionStorage.removeItem(MATCH_PREVIEW_CONTINUE_STORAGE_KEY);
              throw parseErr;
            }
            sessionStorage.removeItem(MATCH_PREVIEW_CONTINUE_STORAGE_KEY);

            const p = profileAction.payload;
            if (h.resumeUrl && typeof h.resumeUrl === "string") {
              const mergedProfile = {
                first_name: p.first_name || userData?.first_name || "",
                last_name: p.last_name || userData?.last_name || "",
                title: p.title || userData?.title || "",
                gender: p.gender || userData?.gender || "",
                dob: p.dob || userData?.dob || "",
                state: p.state || userData?.state || "",
                country: p.country || userData?.country || "",
                industry: p.industry || userData?.industry || "",
                image_url: p.image_url || userData?.image_url || "",
                phone: p.phone || userData?.phone || "",
                links: p.links || userData?.links || [],
                about: p.about || userData?.about || "",
                location: p.location || userData?.location || "",
                isShowOnProfile: 1,
                resume: h.resumeUrl,
                resumeName: h.resumeName || "Resume",
              };
              await httpRequest("post", "/settings/profile/update", mergedProfile);
              await invalidateProfileQuery();
              await getProfileAction(dispatch);
            }
            if (typeof h.jobUrl === "string") {
              setJobUrl(h.jobUrl);
            }
            if (typeof h.jobDescription === "string") {
              setJobDescription(h.jobDescription);
            }
            if (h.activeTab === "url" || h.activeTab === "description") {
              setActiveTab(h.activeTab);
            }
            if (
              (h.resumeUrl && typeof h.resumeUrl === "string") ||
              (typeof h.jobUrl === "string" && h.jobUrl) ||
              (typeof h.jobDescription === "string" && h.jobDescription)
            ) {
              setPreviewHandoffNotice(true);
            }

            const raw = h.rawResponse;
            if (raw && typeof raw === "object" && !Array.isArray(raw)) {
              setMatchResults(raw);
              const tab = h.activeTab === "url" ? "url" : "description";
              const submittedUrl =
                typeof h.jobUrl === "string" ? h.jobUrl : "";
              const submittedDesc =
                typeof h.jobDescription === "string"
                  ? h.jobDescription
                  : "";
              try {
                const saveResult = await saveMatchScoreAction(dispatch, {
                  source_type: tab === "url" ? "url" : "description",
                  job_url: tab === "url" ? submittedUrl : null,
                  job_description:
                    tab === "description" ? submittedDesc : null,
                  job_title:
                    raw?.job_title ||
                    raw?.job_info?.job_title ||
                    null,
                  company_name:
                    raw?.company_name ||
                    raw?.job_info?.hiring_corporation ||
                    null,
                  match_score: raw?.match_score ?? 0,
                  recommendation: raw?.recommendation || null,
                  matched_skills: raw?.matched_skills || [],
                  missing_skills: raw?.missing_skills || [],
                  matched_skills_count: raw?.matched_skills_count || 0,
                  missing_skills_count: raw?.missing_skills_count || 0,
                  culture_fit: raw?.culture_fit || {},
                  details: raw?.details || {},
                  raw_response: raw,
                  credits_consumed: false,
                });
                if (saveResult?.type === ERROR) {
                  cogoToast.error(
                    typeof saveResult.payload === "string"
                      ? saveResult.payload
                      : "Could not save this scan to your history.",
                    { hideAfter: 6 },
                  );
                } else if (
                  saveResult?.type === "SAVE_MATCH_SCORE" &&
                  historyFetched
                ) {
                  const newRecord = {
                    id: saveResult.payload?.id,
                    source_type: tab === "url" ? "url" : "description",
                    job_url: tab === "url" ? submittedUrl : null,
                    job_description:
                      tab === "description" ? submittedDesc : null,
                    job_title:
                      raw?.job_title ||
                      raw?.job_info?.job_title ||
                      null,
                    company_name:
                      raw?.company_name ||
                      raw?.job_info?.hiring_corporation ||
                      null,
                    match_score: raw?.match_score ?? 0,
                    recommendation: raw?.recommendation || null,
                    matched_skills: raw?.matched_skills || [],
                    missing_skills: raw?.missing_skills || [],
                    matched_skills_count: raw?.matched_skills_count || 0,
                    missing_skills_count: raw?.missing_skills_count || 0,
                    culture_fit: raw?.culture_fit || {},
                    details: raw?.details || {},
                    raw_response: raw,
                    credits_consumed: 0,
                    date_created: new Date().toISOString(),
                  };
                  setHistoryRecords((prev) => [newRecord, ...prev]);
                }
              } catch (savePreviewErr) {
                console.error(
                  "Failed to save preview handoff to history:",
                  savePreviewErr,
                );
                cogoToast.error(
                  "Your match is shown below, but it could not be saved to history. Try running the analysis again.",
                  { hideAfter: 6 },
                );
              }
            }
          }
        } catch (handoffErr) {
          console.error("Match preview handoff:", handoffErr);
        }
      } catch (e) {
        console.error("Job Match Score init:", e);
      }
    };
    if (userData?.id) {
      init();
    }
  }, [dispatch, userData?.id, profileData, history]);

  useEffect(() => {
    const savedResumeUrl = profileData?.resume || userData?.resume || "";
    const savedResumeName =
      profileData?.resumeName || userData?.resumeName || "Current resume";
    setResumeUrl(savedResumeUrl);
    setResumeName(savedResumeName);
  }, [profileData, userData]);

  useEffect(() => {
    const key = `jms_campaign_medium_reasoning_credits_${userData?.id || "anonymous"}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw !== null) {
        const parsed = Number(raw);
        if (!Number.isNaN(parsed) && parsed >= 0) {
          setCampaignMediumReasoningCredits(parsed);
        }
      }
    } catch (e) {
      // no-op for storage read issues
    }
  }, [userData?.id]);

  useEffect(() => {
    const key = `jms_campaign_medium_reasoning_credits_${userData?.id || "anonymous"}`;
    try {
      localStorage.setItem(
        key,
        String(Math.max(0, campaignMediumReasoningCredits)),
      );
    } catch (e) {
      // no-op for storage write issues
    }
  }, [campaignMediumReasoningCredits, userData?.id]);

  useEffect(() => {
    const handleFocus = () => {
      if (userData?.id) {
        refreshSubscription();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [userData?.id]);

  useEffect(() => {
    if (subscriptionData?.show_end_of_trial_prompt) {
      setShowTrialEndModal(true);
    }
  }, [subscriptionData?.show_end_of_trial_prompt]);

  const tier = subscriptionData?.tier || "freemium";
  const creditsRemaining = subscriptionData?.credits_remaining || 0;
  const isProHunterActual = tier === "pro_hunter";
  const isProHunter =
    TEST_FLAGS.enabled && TEST_FLAGS.forceIsProHunter !== null
      ? TEST_FLAGS.forceIsProHunter
      : isProHunterActual;
  const isSprintPack = tier === "sprint_pack";
  const isPayPerScan = tier === "pay_per_scan";
  const isFreemiumTier = tier === "freemium";
  const isTrial = Boolean(subscriptionData?.is_trial);
  const trialEndsAt =
    subscriptionData?.trial_ends_at || subscriptionData?.expiry_date;
  const trialDaysLeft = trialEndsAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
      )
    : null;
  const isCreditBased = isSprintPack || isPayPerScan;
  const hasUsableCredits = isProHunter || creditsRemaining > 0;
  const canBuySingleScan = creditsRemaining === 0 && !isProHunter && !isTrial;
  // const canBuySingleScan = !isProHunter && !isSprintPack;
  const hasActiveCampaignActual = Boolean(
    campaign && !campaign.already_claimed,
  );
  const hasActiveCampaign =
    TEST_FLAGS.enabled && TEST_FLAGS.forceShowCampaignBanner !== null
      ? TEST_FLAGS.forceShowCampaignBanner
      : hasActiveCampaignActual;
  const hasClaimedCampaignCredits =
    Boolean(campaign?.already_claimed) && creditsRemaining > 0;
  const hasMediumReasoningAccess =
    isSprintPack ||
    campaignMediumReasoningCredits > 0 ||
    hasClaimedCampaignCredits;
  const analysisReasoning = isProHunter || isSprintPack
    ? "high"
    : hasMediumReasoningAccess
      ? "medium"
      : "low";
  const featureToggles = {
    with_ats: hasMediumReasoningAccess || isProHunter,
    with_rewrite_suggestion: isProHunter || isSprintPack,
    with_keywords: hasMediumReasoningAccess || isProHunter,
    with_recruiter_insights: hasMediumReasoningAccess || isProHunter,
    is_free_user: isFreemiumTier && !hasMediumReasoningAccess && !isProHunter,
  };
  const isBlurred = featureToggles.is_free_user;

  const goToSubscription = () => history.push("/my-subscriptions");
  const goToTrial = () => {
    trackEvent("trial_banner_click", "payment", { page: "job_match_score" });
    history.push("/pro-hunter-trial");
  };

  const BlurGate = ({
    children,
    title,
    subtitle,
    cta = "Upgrade Account",
    placeholderType = "text",
    showCta = true,
  }) => {
    if (!isBlurred) return children;
    const content = isEmptyContent(children) ? (
      <BlurredPlaceholder type={placeholderType} />
    ) : (
      children
    );
    return (
      <div className="jms-blur-gate">
        <div className="jms-blur-content">{content}</div>
        <div className="jms-blur-overlay" onClick={goToSubscription}>
          <p className="jms-blur-title">{title}</p>
          <p className="jms-blur-subtitle">{subtitle}</p>
          {showCta && (
            <button
              type="button"
              className="jms-blur-cta"
              onClick={(e) => {
                e.stopPropagation();
                goToSubscription();
              }}
            >
              {cta}
            </button>
          )}
        </div>
      </div>
    );
  };

  const isEmptyContent = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === "object" && Object.keys(value).length === 0) return true;
    return false;
  };

  const BlurredPlaceholder = ({ type = "text" }) => {
    if (type === "skills") {
      return (
        <div className="jms-skills-list">
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
          <span className="jms-skill-tag jms-skill-matched">&nbsp;</span>
        </div>
      );
    }
    if (type === "detail") {
      return (
        <div className="jms-detail-card">
          <div className="jms-detail-header">
            <span className="jms-detail-title">&nbsp;</span>
          </div>
          <p className="jms-detail-reason">&nbsp;</p>
          <p className="jms-detail-reason">&nbsp;</p>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ height: "14px", background: "#e2e8f0", borderRadius: "4px", width: "100%" }}>&nbsp;</div>
        <div style={{ height: "14px", background: "#e2e8f0", borderRadius: "4px", width: "85%" }}>&nbsp;</div>
        <div style={{ height: "14px", background: "#e2e8f0", borderRadius: "4px", width: "60%" }}>&nbsp;</div>
      </div>
    );
  };

  const activeCampaignData = campaign || {
    name: "Early Bird Promo",
    description: "Get 3 free Job Match Score scans!",
    credits: 3,
  };

  const payPerScanPlan = plans?.find((p) => p.tier === "pay_per_scan");
  const isNigerian = userData?.country == "160";
  const currencySymbol = isNigerian ? "\u20A6" : "$";
  const scanCost = payPerScanPlan
    ? isNigerian
      ? Number(payPerScanPlan.price_ngn || 1000)
      : Number(payPerScanPlan.price_usd || 2.99)
    : isNigerian
      ? 1000
      : 2.99;
  const singleScanPriceDisplay = `${currencySymbol}${numberWithCommas(scanCost.toFixed(2))}`;

  const triggerResumePicker = () => {
    if (!uploadingResume) {
      if (resumeInputRef.current) {
        resumeInputRef.current.click();
      }
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      cogoToast.error("Please upload a PDF or DOC/DOCX resume.");
      return;
    }

    setUploadingResume(true);
    try {
      const uploadResult = await uploadFile(
        file,
        "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload",
      );
      if (!uploadResult?.success || !uploadResult?.data) {
        throw new Error("Upload failed");
      }

      const nextResumeUrl = uploadResult.data;
      const nextResumeName = file.name || uploadResult.fileName || "Resume";
      const mergedProfile = {
        first_name: profileData?.first_name || userData?.first_name || "",
        last_name: profileData?.last_name || userData?.last_name || "",
        title: profileData?.title || userData?.title || "",
        gender: profileData?.gender || userData?.gender || "",
        dob: profileData?.dob || userData?.dob || "",
        state: profileData?.state || userData?.state || "",
        country: profileData?.country || userData?.country || "",
        industry: profileData?.industry || userData?.industry || "",
        image_url: profileData?.image_url || userData?.image_url || "",
        phone: profileData?.phone || userData?.phone || "",
        links: profileData?.links || userData?.links || [],
        about: profileData?.about || userData?.about || "",
        location: profileData?.location || userData?.location || "",
        isShowOnProfile: 1,
        resume: nextResumeUrl,
        resumeName: nextResumeName,
      };

      await httpRequest("post", "/settings/profile/update", mergedProfile);
      await invalidateProfileQuery();
      await getProfileAction(dispatch);

      setResumeUrl(nextResumeUrl);
      setResumeName(nextResumeName);
      cogoToast.success("Resume updated successfully.");
    } catch (uploadErr) {
      console.error("Resume upload error:", uploadErr);
      cogoToast.error("Unable to upload resume. Please try again.");
    } finally {
      setUploadingResume(false);
    }
  };

  // Pro Hunter users see no upsell

  const runAnalyze = async (submittedUrl, submittedDescription, currentTab) => {
    // Check daily scan limit for freemium users
    if (isFreemiumTier && subscriptionData) {
      const canUseScan = subscriptionData.can_use_free_scan ?? true;
      const scansUsed = subscriptionData.daily_scans_used ?? 0;
      const scansLimit = subscriptionData.daily_scans_limit ?? 2;
      
      if (!canUseScan) {
        setError(
          `You've used all ${scansLimit} free scans today. Upgrade to Pro Hunter or Sprint Pack for unlimited scans, or wait until tomorrow for your daily scans to reset.`
        );
        return;
      }
    }

    const buildRunLogPayload = (status, extra = {}) => ({
      run_type: currentTab === "url" ? "external_url" : "external_description",
      status,
      job_url: currentTab === "url" ? submittedUrl : null,
      ...extra,
    });

    setAnalyzing(true);
    setError(null);
    setMatchResults(null);

    try {
      const payload = {
        user_id: userData.id,
        resume_url: profileData?.resume || userData?.resume || resumeUrl || null,
        include_culture_fit: false,
        with_ats: featureToggles.with_ats,
        with_rewrite_suggestion: featureToggles.with_rewrite_suggestion,
        with_keywords: featureToggles.with_keywords,
        with_recruiter_insights: featureToggles.with_recruiter_insights,
        is_free_user:
          TEST_FLAGS.enabled && TEST_FLAGS.forceIsFreeForApi !== null
            ? TEST_FLAGS.forceIsFreeForApi
            : featureToggles.is_free_user,
        reasoning: analysisReasoning,
      };

      if (currentTab === "url") {
        payload.job_url = submittedUrl;
      } else {
        payload.job_description = submittedDescription;
      }

      const matchResult = await postExternalMatchScore(payload);
      if (matchResult.testUnavailable) {
        return;
      }

      if (matchResult.success && matchResult.data) {
        logMatchScoreRunAction(
          buildRunLogPayload("success", {
            match_score: matchResult.data?.match_score ?? null,
            job_title:
              matchResult.data?.job_title ||
              matchResult.data?.job_info?.job_title ||
              null,
            company_name:
              matchResult.data?.company_name ||
              matchResult.data?.job_info?.hiring_corporation ||
              null,
          }),
        );
        setMatchResults(matchResult.data);
        trackMatchScore(
          matchResult.data?.job_title ||
            matchResult.data?.job_info?.job_title ||
            "unknown",
          matchResult.data?.match_score ?? null,
        );
        setJobDescription("");

        let creditConsumed = false;
        const shouldConsumeCredit = !isProHunter && creditsRemaining > 0;
        if (shouldConsumeCredit) {
          try {
            await httpRequest("post", "/job-seeker-subscriptions/use-credit", {
              feature: "job_match_score",
              job_id: null,
              metadata: {
                job_url: submittedUrl || null,
                has_description: !!submittedDescription,
              },
            });
            creditConsumed = true;
            if (campaignMediumReasoningCredits > 0) {
              setCampaignMediumReasoningCredits((prev) => Math.max(0, prev - 1));
            }
            await refreshSubscription();
          } catch (creditErr) {
            console.error("Failed to deduct credit:", creditErr);
          }
        }

        try {
          const saveResult = await saveMatchScoreAction(dispatch, {
            source_type: currentTab === "url" ? "url" : "description",
            job_url: currentTab === "url" ? submittedUrl : null,
            job_description:
              currentTab === "description" ? submittedDescription : null,
            job_title:
              matchResult.data?.job_title ||
              matchResult.data?.job_info?.job_title ||
              null,
            company_name:
              matchResult.data?.company_name ||
              matchResult.data?.job_info?.hiring_corporation ||
              null,
            match_score: matchResult.data?.match_score ?? 0,
            recommendation: matchResult.data?.recommendation || null,
            matched_skills: matchResult.data?.matched_skills || [],
            missing_skills: matchResult.data?.missing_skills || [],
            matched_skills_count: matchResult.data?.matched_skills_count || 0,
            missing_skills_count: matchResult.data?.missing_skills_count || 0,
            culture_fit: matchResult.data?.culture_fit || {},
            details: matchResult.data?.details || {},
            raw_response: matchResult.data,
            credits_consumed: creditConsumed,
          });

          if (saveResult?.type === "SAVE_MATCH_SCORE" && historyFetched) {
            const newRecord = {
              id: saveResult.payload?.id,
              source_type: currentTab === "url" ? "url" : "description",
              job_url: currentTab === "url" ? submittedUrl : null,
              job_description:
                currentTab === "description" ? submittedDescription : null,
              job_title:
                matchResult.data?.job_title ||
                matchResult.data?.job_info?.job_title ||
                null,
              company_name:
                matchResult.data?.company_name ||
                matchResult.data?.job_info?.hiring_corporation ||
                null,
              match_score: matchResult.data?.match_score ?? 0,
              recommendation: matchResult.data?.recommendation || null,
              matched_skills: matchResult.data?.matched_skills || [],
              missing_skills: matchResult.data?.missing_skills || [],
              matched_skills_count: matchResult.data?.matched_skills_count || 0,
              missing_skills_count: matchResult.data?.missing_skills_count || 0,
              culture_fit: matchResult.data?.culture_fit || {},
              details: matchResult.data?.details || {},
              raw_response: matchResult.data,
              credits_consumed: creditConsumed ? 1 : 0,
              date_created: new Date().toISOString(),
            };
            setHistoryRecords((prev) => [newRecord, ...prev]);
          }
        } catch (saveErr) {
          console.error("Failed to save match score history:", saveErr);
        }
      } else {
        logMatchScoreRunAction(
          buildRunLogPayload("failed", {
            error_message: "Match score request did not return results",
          }),
        );
      }
    } catch (err) {
      console.error("Match score error:", err);
      logMatchScoreRunAction(
        buildRunLogPayload("failed", {
          error_message:
            err.response?.data?.detail?.[0]?.msg ||
            err.message ||
            "Failed to analyze job match. Please try again.",
        }),
      );
      setError(
        err.response?.data?.detail?.[0]?.msg ||
          "Failed to analyze job match. Please try again.",
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobUrl && !jobDescription) {
      setError("Please enter a job URL or paste a job description");
      return;
    }

    const hasNonBlankDescription =
      typeof jobDescription === "string" && jobDescription.trim().length > 0;
    // Freemium: unlimited low-reasoning runs when pasting a job description (see job-seeker-subscription.md).
    // Job URL / off-platform style input still consumes a scan credit on freemium.
    const canRunFreemiumBaseline =
      isFreemiumTier && activeTab === "description" && hasNonBlankDescription;

    if (!isProHunter && creditsRemaining <= 0 && !canRunFreemiumBaseline) {
      if (isFreemiumTier && activeTab === "url" && String(jobUrl).trim()) {
        setError(
          "URL-based matches use a scan credit on the free plan. Paste the job description for a basic match (low reasoning), or buy a scan credit.",
        );
      } else {
        setError(
          "No scan credits remaining. Please buy or claim scan credits to continue.",
        );
      }
      setShowBuyCreditModal(true);
      return;
    }

    await runAnalyze(jobUrl, jobDescription, activeTab);
  };

  const toggleHistory = async () => {
    const nextOpen = !historyOpen;
    setHistoryOpen(nextOpen);
    if (nextOpen && !historyFetched) {
      setHistoryLoading(true);
      try {
        const result = await getMatchScoreHistoryAction(dispatch, 1, 20);
        if (result?.type === "GET_MATCH_SCORE_HISTORY") {
          setHistoryRecords(result.payload?.data || []);
          setHistoryFetched(true);
        }
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const viewHistoryRecord = (record) => {
    const rehydrated = record?.raw_response || {
      match_score: record?.match_score,
      recommendation: record?.recommendation,
      matched_skills: record?.matched_skills,
      missing_skills: record?.missing_skills,
      culture_fit: record?.culture_fit,
      details: record?.details,
    };
    setMatchResults(rehydrated);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBuyCredit = async () => {
    setBuyingCredit(true);
    try {
      const result = await buyScanCreditsAction(dispatch, {
        quantity: 1,
      });

      if (result?.type === "ERROR") {
        const errPayload = result.payload;
        if (errPayload?.code === "insufficient_balance") {
          cogoToast.info(
            "Insufficient wallet balance. Please top up your wallet to continue.",
            { hideAfter: 5 },
          );
          setShowBuyCreditModal(false);
          trackFundWalletClick("job_match_score_insufficient_balance");
          history.push("/wallet/add");
          return;
        }

        cogoToast.error(
          typeof errPayload === "string"
            ? errPayload
            : errPayload?.message || "Purchase failed. Please try again.",
        );
        return;
      }

      const payload = result?.payload;

      if (payload?.status) {
        cogoToast.success("Scan credit added to your account.");
        setShowBuyCreditModal(false);
        await refreshSubscription();

        if (pendingAnalyzeAfterBuy && (jobUrl || jobDescription)) {
          setPendingAnalyzeAfterBuy(false);
          await runAnalyze(jobUrl, jobDescription, activeTab);
        }
      } else {
        cogoToast.error(payload?.message || "Purchase failed.");
      }
    } catch (err) {
      console.error("Buy credit error:", err);
      cogoToast.error("Unable to complete purchase. Please try again.");
    } finally {
      setBuyingCredit(false);
    }
  };

  const handleClaimCampaign = async () => {
    if (!campaign?.code) return;
    setClaimingCampaign(true);
    try {
      const result = await claimCampaignCreditsAction(dispatch, campaign.code);
      if (result?.payload?.status) {
        cogoToast.success(result.payload.message || "Free credits claimed!");
        const claimedCredits = Number(
          result?.payload?.credits ||
            result?.payload?.campaign?.credits ||
            activeCampaignData?.credits ||
            3,
        );
        if (!Number.isNaN(claimedCredits) && claimedCredits > 0) {
          setCampaignMediumReasoningCredits(claimedCredits);
        }
        setCampaign((prev) =>
          prev ? { ...prev, already_claimed: true } : null,
        );
        await invalidateCampaignQuery();
        await refreshSubscription();
      } else {
        cogoToast.error(
          typeof result?.payload === "string"
            ? result.payload
            : result?.payload?.message || "Could not claim credits.",
        );
      }
    } catch (err) {
      console.error("Campaign claim error:", err);
      cogoToast.error("Unable to claim credits. Please try again.");
    } finally {
      setClaimingCampaign(false);
    }
  };

  const handleTrialSubscribe = async ({ paymentRail, cardId }) => {
    try {
      if (paymentRail === "jarapay") {
        const result = await convertProHunterTrialJarapay();
        if (result?.requires_payment && result?.checkout_link) {
          window.open(result.checkout_link, "_blank");
          cogoToast.success("Complete JaraPay checkout to subscribe.");
          return;
        }
        if (result?.status === false) {
          cogoToast.error(result.message || "Could not subscribe.");
          history.push("/my-subscriptions");
          return;
        }
        cogoToast.success(result?.message || "ProHunter is now active.");
        setShowTrialEndModal(false);
        await refreshSubscription();
        return;
      }

      const proPlan = plans?.find((p) => p.tier === "pro_hunter");
      if (!proPlan) {
        history.push("/my-subscriptions");
        return;
      }
      const result = await subscribeToJobSeekerPlan(dispatch, {
        plan_id: proPlan.id,
        payment_method: "saved_card",
        card_id: cardId || subscriptionData?.card_id,
      });
      if (result?.payload?.status === false || result?.type === ERROR) {
        cogoToast.error(result?.payload?.message || "Could not charge your card.");
        history.push("/my-subscriptions");
        return;
      }
      cogoToast.success("ProHunter is now active.");
      setShowTrialEndModal(false);
      await refreshSubscription();
    } catch (err) {
      cogoToast.error("Could not subscribe. Opening plans...");
      history.push("/my-subscriptions");
    }
  };

  const handleTrialFeedback = async ({ reasons, other_text }) => {
    try {
      await submitProHunterTrialFeedback({ reasons, other_text });
      setShowTrialEndModal(false);
      await refreshSubscription();
    } catch (err) {
      cogoToast.error("Could not save feedback.");
    }
  };

  const formatHistoryDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (e) {
      return value;
    }
  };

  // Show loading state while fetching subscription
  if (loading) {
    return (
      <Layout>
        <div className="jms-loading-container">
          <CircularProgress size={60} style={{ color: "#1d4ed8" }} />
          <p className="jms-loading-text">
            Loading your subscription details...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="jms-wrapper">
        <div className="jms-page">
          {/* ── Header ── */}
          <div className="jms-header">
            <h2 className="jms-title">Job Match Score</h2>
            <p className="jms-subtitle">
              Complete Step 1 (upload resume), then Step 2 (paste URL or job
              description).
            </p>
            <div className="jms-pricing">
              {isProHunter && isTrial ? (
                <span className="jms-trial-active-badge">
                  ProHunter Trial Active
                  {trialDaysLeft !== null ? ` · ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left` : ""}
                </span>
              ) : isProHunter ? (
                <>
                  <strong style={{ color: "#1d4ed8" }}>Pro Hunter</strong> —
                  Unlimited scans active!
                </>
              ) : isFreemiumTier ? (
                <span className="jms-free-scan-badge">
                  <span className="jms-free-scan-label">
                    {(subscriptionData?.daily_scans_limit ?? 2) -
                      (subscriptionData?.daily_scans_used ?? 0)}{" "}
                    free scan
                    {(subscriptionData?.daily_scans_limit ?? 2) -
                      (subscriptionData?.daily_scans_used ?? 0) ===
                    1
                      ? ""
                      : "s"}{" "}
                    left today
                  </span>
                  <button
                    type="button"
                    className="jms-free-scan-upgrade"
                    onClick={() => {
                      if (trialEligible) {
                        goToTrial();
                        return;
                      }
                      trackEvent("upgrade_click", "payment", {
                        plan_tier: "pro_hunter",
                        page: "job_match_score",
                      });
                      goToSubscription();
                    }}
                  >
                    {trialEligible ? "Activate free trial" : "Upgrade"}
                  </button>
                </span>
              ) : creditsRemaining > 0 ? (
                <>
                  You have <strong>{creditsRemaining}</strong> scan credit
                  {creditsRemaining === 1 ? "" : "s"} remaining.{" "}
                  {!isSprintPack && (
                    <button
                      type="button"
                      className="jms-link-btn"
                      onClick={() => setShowBuyCreditModal(true)}
                    >
                      Buy more
                    </button>
                  )}
                </>
              ) : (
                <>
                  {!isSprintPack ? (
                    <>
                      <button
                        type="button"
                        className="jms-link-btn"
                        onClick={() => {
                          trackEvent("buy_scan_click", "payment", {
                            page: "job_match_score",
                          });
                          setShowBuyCreditModal(true);
                        }}
                      >
                        Buy 1 scan for {singleScanPriceDisplay}
                      </button>{" "}
                      to get started.
                    </>
                  ) : (
                    <>
                      You have <strong>0</strong> scan credits remaining.
                    </>
                  )}
                </>
              )}
            </div>

            {/* ── DEV ONLY: Reset daily scans button ── */}
            {/* Comment out this block when no longer needed */}
            {/* {isFreemiumTier && (
              <button
                type="button"
                className="jms-dev-reset-btn"
                onClick={async () => {
                  try {
                    await httpRequest(
                      "post",
                      "/job-seeker-subscriptions/dev/reset-daily-scans"
                    );
                    cogoToast.success("Daily scans reset to 0.");
                    await refreshSubscription();
                  } catch (err) {
                    cogoToast.error("Reset failed. Make sure you're not in production.");
                  }
                }}
              >
                [DEV] Reset daily scans
              </button>
            )} */}
            {/* ── /DEV ONLY ── */}
          </div>

          {previewHandoffNotice && (
            <div className="jms-preview-handoff-notice" role="status">
              <p>
                Resume and job details from your preview are loaded below.
                Run full analysis when you&apos;re ready (scan credits apply as
                usual).
              </p>
              <button
                type="button"
                className="jms-preview-handoff-dismiss"
                onClick={() => setPreviewHandoffNotice(false)}
              >
                Dismiss
              </button>
            </div>
          )}

          {trialEligible && !isTrial && !isProHunter && !hasActiveCampaign && (
            <ProHunterTrialBanner page="job_match_score" />
          )}

          {/* ── Promo Banner (single-card mode) ── */}
          {hasActiveCampaign ? (
            <div className="jms-campaign-banner">
              <div className="jms-campaign-left">
                <span className="jms-campaign-emoji">🎁</span>
                <div className="jms-campaign-text">
                  <span className="jms-campaign-name">
                    {activeCampaignData.name}
                  </span>
                  <span className="jms-campaign-desc">
                    {activeCampaignData.description ||
                      `Claim ${activeCampaignData.credits} free scan credits!`}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="jms-campaign-claim-btn"
                onClick={TEST_FLAGS.enabled ? undefined : handleClaimCampaign}
                disabled={
                  claimingCampaign || (TEST_FLAGS.enabled && !campaign?.code)
                }
              >
                {claimingCampaign
                  ? "Claiming..."
                  : `Claim ${activeCampaignData.credits} Free Scans`}
              </button>
            </div>
          ) : isTrial ? (
            <div className="jms-trial-unlocked-banner">
              <span className="jms-trial-unlocked-title">
                ProHunter trial unlocked
              </span>
              <span className="jms-trial-unlocked-desc">
                Unlimited scans, full ATS breakdown, AI CV rewrites, and
                recruiter insights — no locks.
              </span>
            </div>
          ) : !isProHunter ? (
            <div className="jms-limited-plan-banner">
            <div className="jms-limited-plan-left">
              <span className="jms-limited-plan-icon">⚠️</span>
              <div className="jms-limited-plan-text">
                <span className="jms-limited-plan-title">
                  {isSprintPack
                    ? "You're on the Sprint Pack"
                    : isPayPerScan
                      ? "You're on the Pay-Per-Scan Plan"
                      : "You're on the Free Plan"}
                </span>
                <span className="jms-limited-plan-desc">
                  You have <strong>{creditsRemaining}</strong>{" "}
                  {isSprintPack || isPayPerScan ? "" : "free "}Advanced Model
                  scan
                  {creditsRemaining === 1 ? "" : "s"} remaining.
                </span>
              </div>
            </div>
            <button
              type="button"
              className="jms-limited-plan-btn"
              onClick={() => {
                if (trialEligible) {
                  goToTrial();
                  return;
                }
                trackEvent("upgrade_click", "payment", {
                  plan_tier: "pro_hunter",
                  page: "job_match_score",
                });
                history.push("/my-subscriptions");
              }}
            >
              {trialEligible ? "Activate free trial" : "Upgrade to Pro"}
            </button>
          </div>
          ) : null}
          {/* ── Buy Scan Credit CTA ── */}
          {canBuySingleScan && (
            <button
              type="button"
              className="jms-buy-credit-btn"
              onClick={() => {
                trackEvent("buy_scan_click", "payment", {
                  page: "job_match_score",
                });
                setShowBuyCreditModal(true);
              }}
              disabled={buyingCredit}
            >
              <FlashOnIcon style={{ fontSize: 17, marginRight: 6 }} />
              {creditsRemaining > 0
                ? `Top up 1 scan credit — ${singleScanPriceDisplay}`
                : `Buy 1 Scan — ${singleScanPriceDisplay}`}
            </button>
          )}

          {/* ── Step 1: Upload Resume ── */}
          <div className="jms-step-heading">
            <span className="jms-step-badge">Step 1</span>
            <span className="jms-step-title">Upload your resume</span>
          </div>
          <div className="jms-resume-cta jms-resume-cta-static">
            <div className="jms-resume-cta-content">
              <div className="jms-resume-cta-left">
                <div className="jms-resume-cta-icon">
                  <DescriptionIcon style={{ fontSize: 20 }} />
                </div>
                <div className="jms-resume-cta-text">
                  <span className="jms-resume-cta-title">
                    Keep your resume up to date
                  </span>
                  <span className="jms-resume-cta-sub">
                    Upload directly here for more accurate ATS and match
                    insights.
                  </span>
                </div>
              </div>
              <div className="jms-resume-inline">
                {resumeUrl ? (
                  <a
                    href={resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jms-resume-current"
                  >
                    Current resume: <strong>{resumeName || "Resume"}</strong>
                  </a>
                ) : (
                  <span className="jms-resume-current-empty">
                    No resume uploaded yet.
                  </span>
                )}
                <input
                  ref={resumeInputRef}
                  type="file"
                  accept="application/pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleResumeUpload}
                />
                <button
                  type="button"
                  className="jms-resume-upload-btn"
                  onClick={triggerResumePicker}
                  disabled={uploadingResume}
                >
                  {uploadingResume
                    ? "Uploading..."
                    : resumeUrl
                      ? "Update Resume"
                      : "Upload Resume"}
                </button>
              </div>
            </div>
          </div>

          {/* ── Step 2: Choose Input Method ── */}
          <div className="jms-step-heading">
            <span className="jms-step-badge">Step 2</span>
            <span className="jms-step-title">
              Choose input method and run analysis
            </span>
          </div>

          {/* ── Tabs + Input Card ── */}
          <div className="jms-card">
            <div className="jms-custom-tabs">
              <div
                className={`jms-custom-tab ${activeTab === "description" ? "jms-custom-tab-active" : ""}`}
                onClick={() => setActiveTab("description")}
              >
                <span className="jms-custom-tab-label">
                  Paste Job Description
                  <span className="jms-tab-badge">Recommended</span>
                </span>
              </div>
              <div
                className={`jms-custom-tab ${activeTab === "url" ? "jms-custom-tab-active" : ""}`}
                onClick={() => setActiveTab("url")}
              >
                Paste Job URL
              </div>
            </div>

            <div className="jms-input-wrap">
              <label className="jms-input-label">
                {activeTab === "url"
                  ? "ENTER JOB LINK"
                  : "PASTE JOB DESCRIPTION"}
              </label>
              {activeTab === "url" ? (
                <input
                  type="url"
                  className="jms-url-input"
                  placeholder="https://www.linkedin.com/jobs/view/..."
                  value={jobUrl}
                  onChange={(e) => setJobUrl(e.target.value)}
                  disabled={analyzing}
                />
              ) : (
                <textarea
                  className="jms-textarea"
                  placeholder="Paste the full job description here..."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  rows={5}
                  disabled={analyzing}
                />
              )}
            </div>

            {error && <div className="jms-error-message">{error}</div>}

            <button
              className="jms-analyze-btn"
              onClick={handleAnalyze}
              disabled={analyzing || (!jobUrl && !jobDescription)}
            >
              {analyzing ? (
                <>
                  <CircularProgress
                    size={17}
                    style={{ color: "#fff", marginRight: 8 }}
                  />
                  Analyzing...
                </>
              ) : (
                <>
                  <TrackChangesIcon style={{ fontSize: 17, marginRight: 8 }} />
                  Match and Analyze
                </>
              )}
            </button>
          </div>

          {/* ── Match Results ── */}
          {matchResults && (
            <div className="jms-results-container">
              <div className="jms-results-header">
                <h3>Match Analysis Results</h3>
              </div>

              {/* Top Row: Match Score Circle & Job Info */}
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  flexWrap: "wrap",
                  marginBottom: "24px",
                  alignItems: "center",
                }}
              >
                {/* Match Score Circle */}
                <div
                  className="jms-score-circle"
                  style={{ marginBottom: 0, flexShrink: 0 }}
                >
                  <div className="jms-circle-wrapper">
                    <svg className="jms-circle-svg" viewBox="0 0 120 120">
                      <circle
                        className="jms-circle-bg"
                        cx="60"
                        cy="60"
                        r="54"
                      />
                      <circle
                        className="jms-circle-progress"
                        cx="60"
                        cy="60"
                        r="54"
                        style={{
                          strokeDasharray: `${(matchResults.match_score / 100) * 339.292} 339.292`,
                        }}
                      />
                    </svg>
                    <div className="jms-score-text">
                      <span className="jms-score-number">
                        {matchResults.match_score}%
                      </span>
                      <span className="jms-score-label">Match Score</span>
                    </div>
                  </div>
                </div>

                {/* Job Information */}
                {matchResults.job_info && (
                  <div
                    className="jms-job-info"
                    style={{ marginBottom: 0, flex: 1, minWidth: "250px" }}
                  >
                    <h4
                      style={{
                        margin: "0 0 10px",
                        color: "#0f172a",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      Job Information
                    </h4>
                    <div className="jms-job-info-grid">
                      <div>
                        <span className="jms-job-info-label">Title</span>
                        <p>{matchResults.job_info.job_title || "N/A"}</p>
                      </div>
                      <div>
                        <span className="jms-job-info-label">Company</span>
                        <p>
                          {matchResults.job_info.hiring_corporation || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="jms-job-info-label">Location</span>
                        <p>{matchResults.job_info.job_location || "N/A"}</p>
                      </div>
                      <div>
                        <span className="jms-job-info-label">Salary</span>
                        <p>{matchResults.job_info.salary || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Recommendation */}
              <div className="jms-recommendation">
                <h4>Recommendation</h4>
                <p className={isBlurred ? "jms-recommendation-preview" : ""}>
                  {isBlurred
                    ? (matchResults.recommendation || "Based on your profile, you have a moderate match with this role. Upgrade to see the full recommendation.")
                    : matchResults.recommendation}
                </p>
              </div>

              {/* Skills Section */}
              {(matchResults.matched_skills?.length > 0 ||
                matchResults.missing_skills?.length > 0 ||
                matchResults.matched_skills_count > 0 ||
                matchResults.missing_skills_count > 0) && (
                <div className="jms-skills-section">
                  {(matchResults.matched_skills?.length > 0 || matchResults.matched_skills_count > 0) && (
                    <div className="jms-skills-matched">
                      <h4>
                        ✓ Matched Skills
                        {matchResults.matched_skills_count > 0 && ` (${matchResults.matched_skills_count})`}
                      </h4>
                      {matchResults.matched_skills?.length > 0 ? (
                        <div className="jms-skills-list">
                          {matchResults.matched_skills.map((skill, idx) => (
                            <span
                              key={idx}
                              className="jms-skill-tag jms-skill-matched"
                            >
                              {skill || "\u00A0"}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 0" }}>
                          Upgrade to see all matched skills.
                        </p>
                      )}
                    </div>
                  )}

                  {(matchResults.missing_skills?.length > 0 || matchResults.missing_skills_count > 0) && (
                    <div className="jms-skills-missing">
                      <h4>
                        ⚠ Missing Skills
                        {matchResults.missing_skills_count > 0 && ` (${matchResults.missing_skills_count})`}
                      </h4>
                      {matchResults.missing_skills?.length > 0 ? (
                        <div className="jms-skills-list">
                          {matchResults.missing_skills.map((skill, idx) => (
                            <span
                              key={idx}
                              className="jms-skill-tag jms-skill-missing"
                            >
                              {skill || "\u00A0"}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 0" }}>
                          Upgrade to see all missing skills.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ATS + Recruiter Insights */}
              {matchResults.ats_analysis && (
                <div className="jms-ats-panel">
                  <div
                    className="jms-ats-header"
                    style={{
                      cursor: "pointer",
                      marginBottom: atsOpen ? "16px" : "0",
                    }}
                    onClick={() => setAtsOpen(!atsOpen)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>ATS Analysis</h4>
                      <span className="jms-ats-score">
                        {`${matchResults.ats_analysis.overall_ats_score ?? 0}/100 ATS`}
                      </span>
                    </div>
                    {atsOpen ? (
                      <ExpandLessIcon style={{ color: "#f8fafc" }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: "#f8fafc" }} />
                    )}
                  </div>

                  {atsOpen && (
                    <>
                      <div className="jms-ats-metrics">
                        <div className="jms-ats-metric">
                          <span className="jms-ats-metric-label">
                            Parse confidence
                          </span>
                          <span className="jms-ats-metric-value">
                            {matchResults.ats_analysis.parse_confidence ||
                              "N/A"}
                          </span>
                        </div>
                        <div className="jms-ats-metric">
                          <span className="jms-ats-metric-label">
                            Keyword match rate
                          </span>
                          <span className="jms-ats-metric-value">
                            {matchResults.ats_analysis.keyword_match_rate ?? 0}%
                          </span>
                        </div>
                      </div>

                      {Array.isArray(matchResults.ats_analysis.issues) &&
                        matchResults.ats_analysis.issues.length > 0 && (
                          <div className="jms-ats-issues">
                            <h5>ATS Issues</h5>
                            {matchResults.ats_analysis.issues.map(
                              (issue, idx) => (
                                <div className="jms-ats-issue" key={idx}>
                                  <div className="jms-ats-issue-head">
                                    <span>{issue.category || "Issue"}</span>
                                    <span
                                      className={`jms-ats-severity ${String(issue.severity || "").toLowerCase()}`}
                                    >
                                      {issue.severity || "N/A"}
                                    </span>
                                  </div>
                                  <p>{issue.description}</p>
                                  {issue.recommendation && (
                                    <small>
                                      Recommendation: {issue.recommendation}
                                    </small>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}

                      {Array.isArray(
                        matchResults.ats_analysis.top_recommendations,
                      ) &&
                        matchResults.ats_analysis.top_recommendations.length >
                          0 && (
                          <div className="jms-ats-top-recs">
                            <h5>Top Recommendations</h5>
                            <ul>
                              {matchResults.ats_analysis.top_recommendations.map(
                                (item, idx) => (
                                  <li key={idx}>{item}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}

              {/* Details */}
              {(matchResults.details &&
                Object.keys(matchResults.details).length > 0) ||
                isBlurred ? (
                <div className="jms-details">
                  <h4>📋 Detailed Breakdown</h4>
                  <div className="jms-details-grid">
                    {(matchResults.details &&
                      Object.keys(matchResults.details).length > 0) ? (
                      Object.entries(matchResults.details).map(
                        ([key, value]) => {
                          const normalizedKey = String(key).toLowerCase();
                          const isVisibleForFree =
                            normalizedKey === "job title" ||
                            normalizedKey === "qualifications";
                          const card = (
                            <div key={key} className="jms-detail-card">
                              <div className="jms-detail-header">
                                <span className="jms-detail-title">{key}</span>
                                {value?.score !== undefined && (
                                  <span className="jms-detail-score">
                                    {value.score}/{value.max || 100}
                                  </span>
                                )}
                              </div>
                              {value?.reason ? (
                                <p className="jms-detail-reason">
                                  {value.reason}
                                </p>
                              ) : (
                                <p className="jms-detail-reason">
                                  &nbsp;
                                </p>
                              )}
                            </div>
                          );
                          return isVisibleForFree ? (
                            card
                          ) : (
                            <BlurGate
                              key={key}
                              title={`Unlock ${key}`}
                              subtitle="Upgrade to see the full breakdown for this section."
                              cta="Upgrade Account"
                              placeholderType="detail"
                              showCta={false}
                            >
                              {card}
                            </BlurGate>
                          );
                        },
                      )
                    ) : (
                      <>
                        <BlurGate
                          title="Unlock Job Fit Details"
                          subtitle="Upgrade to see the full detailed breakdown for this role."
                          cta="Upgrade Account"
                          placeholderType="detail"
                          showCta={false}
                        >
                          <BlurredPlaceholder type="detail" />
                        </BlurGate>
                        <BlurGate
                          title="Unlock Skills Alignment"
                          subtitle="Upgrade to see how your skills align with this role."
                          cta="Upgrade Account"
                          placeholderType="detail"
                          showCta={false}
                        >
                          <BlurredPlaceholder type="detail" />
                        </BlurGate>
                        <BlurGate
                          title="Unlock Experience Match"
                          subtitle="Upgrade to see how your experience matches the role requirements."
                          cta="Upgrade Account"
                          placeholderType="detail"
                          showCta={false}
                        >
                          <BlurredPlaceholder type="detail" />
                        </BlurGate>
                      </>
                    )}
                  </div>
                </div>
              ) : null}

              {(matchResults.recruiter_insights ||
                matchResults.ats_analysis?.recruiter_insights) && (
                <div className="jms-recruiter-panel">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: recruiterOpen ? "16px" : "0",
                    }}
                    onClick={() => setRecruiterOpen(!recruiterOpen)}
                  >
                    <h4 style={{ margin: 0 }}>Recruiter Insights</h4>
                    {recruiterOpen ? (
                      <ExpandLessIcon style={{ color: "#f8fafc" }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: "#f8fafc" }} />
                    )}
                  </div>
                  {recruiterOpen && (
                    <>
                      <p>
                        {matchResults.recruiter_insights?.summary ||
                          matchResults.ats_analysis?.recruiter_insights
                            ?.summary ||
                          "No summary provided."}
                      </p>
                      <div className="jms-recruiter-breakdown">
                        {(
                          matchResults.recruiter_insights?.breakdown ||
                          matchResults.ats_analysis?.recruiter_insights
                            ?.breakdown ||
                          []
                        ).map((item, idx) => (
                          <div className="jms-recruiter-item" key={idx}>
                            <span>{item.dimension}</span>
                            <small>{item.finding}</small>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {(Array.isArray(matchResults.keyword_suggestions) &&
                matchResults.keyword_suggestions.length > 0) ||
              (Array.isArray(matchResults.ats_analysis?.keyword_suggestions) &&
                matchResults.ats_analysis.keyword_suggestions.length > 0) ? (
                <div className="jms-keywords-panel">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: keywordsOpen ? "16px" : "0",
                    }}
                    onClick={() => setKeywordsOpen(!keywordsOpen)}
                  >
                    <h4 style={{ margin: 0 }}>Keyword Suggestions</h4>
                    {keywordsOpen ? (
                      <ExpandLessIcon style={{ color: "#f8fafc" }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: "#f8fafc" }} />
                    )}
                  </div>
                  {keywordsOpen && (
                    <div className="jms-keyword-list">
                      {(
                        matchResults.keyword_suggestions ||
                        matchResults.ats_analysis?.keyword_suggestions ||
                        []
                      ).map((item, idx) => (
                        <div className="jms-keyword-card" key={idx}>
                          <div className="jms-keyword-row">
                            <span className="jms-keyword-pill">
                              {item.keyword}
                            </span>
                            <span className="jms-keyword-priority">
                              {item.priority}
                            </span>
                          </div>
                          {item.context && <p>{item.context}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {Array.isArray(matchResults.ats_analysis?.rewrite_suggestions) &&
                matchResults.ats_analysis.rewrite_suggestions.length > 0 && (
                  <div className="jms-rewrite-panel">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        marginBottom: rewriteOpen ? "16px" : "0",
                      }}
                      onClick={() => setRewriteOpen(!rewriteOpen)}
                    >
                      <h4 style={{ margin: 0 }}>AI Rewrite Suggestions</h4>
                      {rewriteOpen ? (
                        <ExpandLessIcon style={{ color: "#f8fafc" }} />
                      ) : (
                        <ExpandMoreIcon style={{ color: "#f8fafc" }} />
                      )}
                    </div>
                    {rewriteOpen && (
                      <>
                        {matchResults.ats_analysis.rewrite_suggestions.map(
                          (item, idx) => (
                            <div className="jms-rewrite-card" key={idx}>
                              <div className="jms-rewrite-head">
                                <span>
                                  {item.section || `Section ${idx + 1}`}
                                </span>
                              </div>
                              <p className="jms-rewrite-original">
                                {item.original}
                              </p>
                              <p className="jms-rewrite-new">{item.rewrite}</p>
                              {item.reason && <small>{item.reason}</small>}
                            </div>
                          ),
                        )}
                      </>
                    )}
                  </div>
                )}

              {isBlurred && (
                <div className="jms-upsell-banner">
                  <div className="jms-upsell-banner-content">
                    <p style={{ fontWeight: "600" }}>
                      Get ATS analysis, recruiter's insight and CV rewrites in
                      minutes.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="jms-upsell-banner-btn"
                    onClick={goToSubscription}
                  >
                    Upgrade Account
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Previous Scans (collapsible) ── */}
          <div className="jms-history-wrap">
            <button
              type="button"
              className="jms-history-toggle"
              onClick={toggleHistory}
            >
              <span className="jms-history-toggle-left">
                <HistoryIcon style={{ fontSize: 18, marginRight: 8 }} />
                Previous Scans
              </span>
              {historyOpen ? (
                <ExpandLessIcon style={{ fontSize: 20 }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: 20 }} />
              )}
            </button>

            {historyOpen && (
              <div className="jms-history-body">
                {historyLoading && (
                  <div className="jms-history-loading">
                    <CircularProgress size={22} style={{ color: "#1d4ed8" }} />
                    <span>Loading your previous scans…</span>
                  </div>
                )}

                {!historyLoading && historyRecords.length === 0 && (
                  <div className="jms-history-empty">
                    You haven&apos;t run any match score analyses yet. Your
                    saved scans will appear here.
                  </div>
                )}

                {!historyLoading && historyRecords.length > 0 && (
                  <ul className="jms-history-list">
                    {historyRecords.map((record) => {
                      const score = Number(record.match_score || 0);
                      const label =
                        record.job_title ||
                        record.company_name ||
                        (record.source_type === "url"
                          ? record.job_url
                          : "Pasted job description");
                      return (
                        <li key={record.id} className="jms-history-item">
                          <div
                            className={`jms-history-score ${score >= 70 ? "good" : score >= 50 ? "ok" : "low"}`}
                          >
                            {Math.round(score)}%
                          </div>
                          <div className="jms-history-meta">
                            <span className="jms-history-title" title={label}>
                              {label}
                            </span>
                            <span className="jms-history-date">
                              {formatHistoryDate(record.date_created)}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="jms-history-view"
                            onClick={() => viewHistoryRecord(record)}
                          >
                            View
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* ── Bottom Stats ── */}
          <div className="jms-stats-grid">
            <div className="jms-stat-card jms-stat-amber">
              <div className="jms-stat-icon">
                <EmojiEventsIcon style={{ fontSize: 20 }} />
              </div>
              <div className="jms-stat-body">
                <span className="jms-stat-label">SCAN CREDITS</span>
                {isProHunter ? (
                  <>
                    <p className="jms-stat-value">Unlimited scans</p>
                    <p className="jms-stat-remaining">
                      <strong style={{ color: "#15803d" }}>∞ Unlimited</strong>
                    </p>
                  </>
                ) : creditsRemaining > 0 ? (
                  <>
                    <p className="jms-stat-value">1 credit per scan</p>
                    <p className="jms-stat-remaining">
                      Credits remaining: <strong>{creditsRemaining}</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="jms-stat-value">No credits available</p>
                    <p className="jms-stat-remaining">
                      {!isSprintPack ? (
                        <button
                          type="button"
                          className="jms-link-btn"
                          onClick={() => setShowBuyCreditModal(true)}
                        >
                          Buy scan credits
                        </button>
                      ) : (
                        <>
                          Credits remaining: <strong>0</strong>
                        </>
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Buy 1 Scan Credit Modal ── */}
      {showBuyCreditModal && (
        <div
          className="jms-modal-overlay"
          onClick={() => {
            if (!buyingCredit) {
              setShowBuyCreditModal(false);
              setPendingAnalyzeAfterBuy(false);
            }
          }}
        >
          <div className="jms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="jms-modal-header">
              <h3 className="jms-modal-title">Buy 1 Scan Credit</h3>
              <button
                type="button"
                className="jms-modal-close"
                onClick={() => {
                  if (!buyingCredit) {
                    setShowBuyCreditModal(false);
                    setPendingAnalyzeAfterBuy(false);
                  }
                }}
                aria-label="Close"
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            <p className="jms-modal-sub">
              Get a single Job Match Score scan for{" "}
              <strong>{singleScanPriceDisplay}</strong>. No subscription
              required. Payment is deducted from your wallet.
            </p>

            {(jobUrl || jobDescription) && (
              <label className="jms-modal-autorun">
                <input
                  type="checkbox"
                  checked={pendingAnalyzeAfterBuy}
                  onChange={(e) => setPendingAnalyzeAfterBuy(e.target.checked)}
                  disabled={buyingCredit}
                />
                <span>Run the match score immediately after purchase</span>
              </label>
            )}

            <div className="jms-modal-actions">
              <button
                type="button"
                className="jms-modal-secondary"
                onClick={() => {
                  setShowBuyCreditModal(false);
                  setPendingAnalyzeAfterBuy(false);
                }}
                disabled={buyingCredit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="jms-modal-primary"
                onClick={handleBuyCredit}
                disabled={buyingCredit}
              >
                {buyingCredit ? (
                  <>
                    <CircularProgress
                      size={15}
                      style={{ color: "#fff", marginRight: 8 }}
                    />
                    Processing…
                  </>
                ) : (
                  `Pay ${singleScanPriceDisplay}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <CustomModal
        isOpen={showTrialWelcome}
        onClose={() => setShowTrialWelcome(false)}
        title="ProHunter trial is active"
        maxWidth="420px"
        compact
      >
        <div className="jms-trial-welcome">
          <div className="jms-trial-welcome-badge">30 days · no charge</div>
          <p className="jms-trial-welcome-lead">
            You now have full ProHunter access. Scan jobs without limits until
            your trial ends.
          </p>
          <ul className="jms-trial-welcome-perks">
            <li>Unlimited match scores</li>
            <li>ATS insights</li>
            <li>AI CV rewrites</li>
            <li>Recruiter view</li>
          </ul>
          <p className="jms-trial-welcome-note">
            You will not be charged during the trial.
          </p>
          <button
            type="button"
            className="jms-modal-primary jms-trial-welcome-cta"
            onClick={() => setShowTrialWelcome(false)}
          >
            Start scanning
          </button>
        </div>
      </CustomModal>
      <ProHunterTrialEndModal
        isOpen={showTrialEndModal}
        paymentRail={subscriptionData?.payment_rail}
        cardId={subscriptionData?.card_id}
        onSubscribe={handleTrialSubscribe}
        onSubmitFeedback={handleTrialFeedback}
        onClose={() => setShowTrialEndModal(false)}
      />
    </Layout>
  );
};

export default JobMatchScore;
